import { httpClient } from "./client";
import type { UserApi } from "./interfaces";
import type {
  BookingResponse,
  GroupCreate,
  GroupUpdate,
  GroupResponse,
  GroupInvitationResponse,
  UUID,
} from "../types";
import type {
  // UserProfile DTOs come from the generated barrel (#1311) — the real contract
  // (account_status / requires_parental_consent REQUIRED), so callers no longer
  // narrow the response at the onboarding boundary. ADR-005 / clean-migration.
  // (createProfile's body type is inherited from the UserApi interface, which
  // relaxes the server-defaulted `visibility` — so UserProfileCreate isn't named
  // directly here.)
  UserProfileUpdate,
  UserProfileResponse,
  MyBookingInvitationResponse,
  BookingInvitationResponse,
  BookingInvitationGroupResponse,
  PlayerAgendaPage,
  PlayerProfileSummaryResponse,
} from "../types/generated";

export const userApi: UserApi = {
  getMyAgenda: (params = {}, signal) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) query.set(key, String(value));
    }
    const suffix = query.toString();
    return httpClient.get<PlayerAgendaPage>(
      `/users/me/agenda${suffix ? `?${suffix}` : ""}`, { signal },
    );
  },
  // `data` type is inherited from UserApi.createProfile (generated UserProfileCreate
  // with the server-defaulted `visibility` relaxed to optional — the honest wire
  // body, see the interface note). No local annotation so it tracks that contract.
  createProfile: (data) =>
    httpClient.post<UserProfileResponse>("/users/profile", data),

  getMyProfile: () =>
    httpClient.get<UserProfileResponse>("/users/profile"),

  getProfileSummary: (userId: string) =>
    httpClient.get<PlayerProfileSummaryResponse>(
      `/users/${encodeURIComponent(userId)}/profile-summary`,
    ),
  getProfile: (userId: string) =>
    httpClient.get<UserProfileResponse>(`/users/${userId}/profile`),

  updateProfile: (data: UserProfileUpdate) =>
    httpClient.patch<UserProfileResponse>("/users/profile", data),

  deleteProfile: () =>
    httpClient.delete<void>("/users/profile"),

  addFavourite: (facilityId: string) =>
    httpClient.post<void>(`/users/favourites/${facilityId}`),

  removeFavourite: (facilityId: string) =>
    httpClient.delete<void>(`/users/favourites/${facilityId}`),

  listFavourites: () =>
    httpClient.get<string[]>("/users/favourites"),

  createGroup: (data: GroupCreate) =>
    httpClient.post<GroupResponse>("/users/groups", data),

  getGroup: (groupId: UUID) =>
    httpClient.get<GroupResponse>(`/users/groups/${groupId}`),

  updateGroup: (groupId: UUID, data: GroupUpdate) =>
    httpClient.patch<GroupResponse>(`/users/groups/${groupId}`, data),

  deleteGroup: (groupId: UUID) =>
    httpClient.delete<void>(`/users/groups/${groupId}`),

  leaveGroup: (groupId: UUID) =>
    httpClient.post<void>(`/users/groups/${groupId}/leave`),

  inviteToGroup: (groupId: UUID, inviteeId: string) =>
    httpClient.post<GroupInvitationResponse>(`/users/groups/${groupId}/invite?invitee_id=${encodeURIComponent(inviteeId)}`),

  acceptInvitation: (invitationId: UUID) =>
    httpClient.post<void>(`/users/invitations/${invitationId}/accept`),

  declineInvitation: (invitationId: UUID) =>
    httpClient.post<void>(`/users/invitations/${invitationId}/decline`),

  listPendingInvitations: () =>
    httpClient.get<GroupInvitationResponse[]>("/users/invitations"),

  listMyBookings: () =>
    httpClient.get<BookingResponse[]>("/me/bookings"),

  // The caller's INCOMING (pending) court-booking invitations — "who has invited
  // me to a court?". Server-scoped to the token user (no uid in the path/query,
  // so no IDOR surface). Canonical /users/me path (not the bare /me the BFF
  // rewrites), mirroring listMyPaymentMethods / listMyLeagueRegistrations.
  listMyBookingInvitations: () =>
    httpClient.get<MyBookingInvitationResponse[]>(
      "/users/me/booking-invitations",
    ),

  // Accept / decline EVERY pending invitation in a recurring booking group in one
  // call. Canonical /users/me path (not the bare /me the BFF rewrites), mirroring
  // listMyBookingInvitations. The body carries ONLY the recurring_group_id — the
  // server scopes to the token user (no uid → no IDOR surface).
  acceptBookingInvitationGroup: (data: BookingInvitationGroupResponse) =>
    httpClient.post<BookingInvitationResponse[]>(
      "/users/me/booking-invitations/accept-group",
      data,
    ),

  declineBookingInvitationGroup: (data: BookingInvitationGroupResponse) =>
    httpClient.post<BookingInvitationResponse[]>(
      "/users/me/booking-invitations/decline-group",
      data,
    ),
};
